import { useState, useCallback } from 'react';
import axios from 'axios';
import { EDUCATION_ENDPOINTS, STORAGE_KEYS } from '../constants/api';
import {
  DocumentTemplate,
  TemplateListResponse
} from '../types/EducationHub';

interface TemplateFilters {
  category?: string;
  documentTypeId?: string;
  limit?: number;
  offset?: number;
}

// Custom hook for template management
export const useTemplates = () => {
  const [templates, setTemplates] = useState<DocumentTemplate[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [totalCount, setTotalCount] = useState(0);
  const [hasMore, setHasMore] = useState(false);

  const getAuthHeaders = () => {
    const token = sessionStorage.getItem(STORAGE_KEYS.TOKEN);
    return token ? { Authorization: `Bearer ${token}` } : {};
  };

  const fetchTemplates = useCallback(async (userID: string) => {
    setLoading(true);
    setError(null);
    
    try {
      const response = await axios.get(EDUCATION_ENDPOINTS.TEMPLATES, { 
					headers: getAuthHeaders(),
				// backend expects `userId` (camelCase); pass the value under that key
				params: { userId: userID } // Get all templates (admin may pass another userId)
      }).catch(() => ({ data: { templates: [] } })) // Gracefully handle if templates endpoint fails

      const data = response.data;
      setTemplates(data.templates || []);
      setTotalCount(data.total || 0);
      setHasMore(data.hasMore || false);
    } catch (err: any) {
      setTemplates([]);
      setTotalCount(0);
      setHasMore(false);
      
      if (err.response?.status !== 401 && err.response?.status !== 403) {
        setError(err.response?.data?.error || 'Failed to fetch templates');
        console.error('Error fetching templates:', err);
      }
    } finally {
      setLoading(false);
    }
  }, []);

  const getTemplateById = useCallback(async (id: string): Promise<DocumentTemplate | null> => {
    setLoading(true);
    setError(null);
    
    try {
      const response = await axios.get<DocumentTemplate>(`${EDUCATION_ENDPOINTS.TEMPLATES}/${id}`, {
        headers: getAuthHeaders()
      });
      
      return response.data;
    } catch (err: any) {
      setError(err.response?.data?.error || 'Failed to fetch template');
      console.error('Error fetching template:', err);
      return null;
    } finally {
      setLoading(false);
    }
  }, []);

  const getTemplateByDocumentType = useCallback(async (documentTypeId: string): Promise<DocumentTemplate | null> => {
    setLoading(true);
    setError(null);
    
    try {
      const response = await axios.get<DocumentTemplate>(
        `${EDUCATION_ENDPOINTS.TEMPLATES_BY_DOCTYPE}/${documentTypeId}`,
        { headers: getAuthHeaders() }
      );
      
      return response.data;
    } catch (err: any) {
      // Don't set error for 404 - just means no template exists
      if (err.response?.status === 404) {
        return null;
      }
      setError(err.response?.data?.error || 'Failed to fetch template');
      console.error('Error fetching template by document type:', err);
      return null;
    } finally {
      setLoading(false);
    }
  }, []);

  const getTemplateCategories = useCallback(async (): Promise<string[]> => {
    try {
      const response = await axios.get<{ categories: string[] }>(
        EDUCATION_ENDPOINTS.TEMPLATES_CATEGORIES,
        { headers: getAuthHeaders() }
      );
      
      return response.data.categories || [];
    } catch (err: any) {
      console.error('Error fetching template categories:', err);
      return [];
    }
  }, []);

  const downloadTemplate = useCallback(async (resourceId: string, options?: { suggestedFilename?: string; fileType?: string }) => {
    try {
			const token = sessionStorage.getItem(STORAGE_KEYS.TOKEN);
			if (!token) throw new Error('No auth token found');

			const headers = {
				Authorization: `Bearer ${token}`,
			};

			// Make request for the file as a blob
			const response = await axios.get(
				EDUCATION_ENDPOINTS.TEMPLATES_DOWNLOAD(resourceId),
				{ headers, responseType: 'blob' }
			);
      // Parse filename from Content-Disposition header if present
      const disposition = response.headers['content-disposition'] || response.headers['Content-Disposition'];
      let parsedFilename: string | null = null;
      if (disposition) {
        const filenameStarMatch = disposition.match(/filename\*\s*=\s*([^;]+)/i);
        const filenameMatch = disposition.match(/filename\s*=\s*\"([^\"]+)\"|filename\s*=\s*([^;\n\r]+)/i);
        if (filenameStarMatch) {
          try {
            const raw = filenameStarMatch[1].trim();
            const parts = raw.split("''");
            const encoded = parts.length > 1 ? parts.slice(1).join("''") : raw;
            parsedFilename = decodeURIComponent(encoded.replace(/^['\"]|['\"]$/g, ''));
          } catch (e) {
            // ignore and fallback
          }
        } else if (filenameMatch) {
          const f = filenameMatch[1] || filenameMatch[2];
          if (f) parsedFilename = f.replace(/^['\"]|['\"]$/g, '');
        }
      }

      const finalFilename = parsedFilename || 'download';

      // Trigger download using the blob and filename
      const url = window.URL.createObjectURL(response.data);
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', finalFilename);
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch (err: any) {
      console.error('Error downloading resource:', err);
    }
  }, []);

  const downloadTemplateByDocID = useCallback(async (docTypeId: string, selectedUserId: string, options?: { suggestedFilename?: string; fileType?: string }) => {
      try {
			const token = sessionStorage.getItem(STORAGE_KEYS.TOKEN);
			if (!token) throw new Error('No auth token found');

			const headers = {
				Authorization: `Bearer ${token}`,
			};

			// Make request for the file as a blob
			const response = await axios.get(
				EDUCATION_ENDPOINTS.TEMPLATES_DOCTYPE_DOWNLOAD(docTypeId, selectedUserId),
				{ headers, responseType: 'blob' }
			);
      
      // 2. Parse filename from Content-Disposition header if present
      const disposition = response.headers['content-disposition'] || response.headers['Content-Disposition'];
      let parsedFilename: string | null = null;
      if (disposition) {
        const filenameStarMatch = disposition.match(/filename\*\s*=\s*([^;]+)/i);
        const filenameMatch = disposition.match(/filename\s*=\s*\"([^\"]+)\"|filename\s*=\s*([^;\n\r]+)/i);
        if (filenameStarMatch) {
          try {
            const raw = filenameStarMatch[1].trim();
            const parts = raw.split("''");
            const encoded = parts.length > 1 ? parts.slice(1).join("''") : raw;
            parsedFilename = decodeURIComponent(encoded.replace(/^['\"]|['\"]$/g, ''));
          } catch (e) {
            // ignore and fallback
          }
        } else if (filenameMatch) {
          const f = filenameMatch[1] || filenameMatch[2];
          if (f) parsedFilename = f.replace(/^['\"]|['\"]$/g, '');
        }
      }

      // 3. Use server filename if available, otherwise fallback to provided suggestion
      const finalFilename = parsedFilename || 'download';

      // 4. Trigger download using the blob and filename
      const url = window.URL.createObjectURL(response.data);
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', finalFilename);
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch (err: any) {
      console.error('Error downloading resource:', err);
    }
  }, []);

  return {
    templates,
    loading,
    error,
    totalCount,
    hasMore,
    fetchTemplates,
    getTemplateById,
    getTemplateByDocumentType,
    getTemplateCategories,
    downloadTemplate,
    downloadTemplateByDocID
  };
};
